import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen font-sans text-slate-900 bg-white overflow-x-hidden selection:bg-blue-200 scroll-smooth">
      
      {/* --- NAVIGATION --- */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo(0,0)}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl shadow-lg ${scrolled ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'}`}>
              S
            </div>
            <span className={`text-2xl font-display font-black tracking-tight ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              SalesWork CRM
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className={`text-sm font-bold transition-colors ${scrolled ? 'text-slate-600 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>Funksjoner</Link>
            <a href="#pricing" className={`text-sm font-bold transition-colors ${scrolled ? 'text-slate-600 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>Priser</a>
            <Link to="/faq" className={`text-sm font-bold transition-colors ${scrolled ? 'text-slate-600 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>Q&A</Link>
            
            <div className={`w-px h-6 ${scrolled ? 'bg-slate-200' : 'bg-white/20'}`}></div>
            
            <Link to="/book-demo" className={`flex items-center gap-2 text-sm font-bold transition-colors ${scrolled ? 'text-slate-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
               <SafeIcon icon="🎥" /> Book Demo
            </Link>

            <Link to="/login" className={`text-sm font-bold transition-colors ${scrolled ? 'text-slate-900 hover:text-blue-600' : 'text-white hover:text-blue-200'}`}>
              Logg inn
            </Link>
            <Link to="/signup" className={`px-6 py-3 rounded-xl text-sm font-bold shadow-lg transition-all hover:scale-105 active:scale-95 ${scrolled ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-white text-slate-900 hover:bg-slate-100'}`}>
              Prøv Gratis
            </Link>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-40 bg-[#1e293b] overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-blue-300 text-xs font-black uppercase tracking-widest mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
            Norges nye standard for B2B Salg
          </motion.div>
          
          <motion.h1 initial="hidden" animate="visible" variants={fadeIn} transition={{ delay: 0.1 }} className="text-5xl lg:text-7xl font-display font-black text-white leading-[1.1] mb-8 tracking-tight max-w-5xl mx-auto">
            Lukk flere avtaler, <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-emerald-400">
              helt uten friksjon.
            </span>
          </motion.h1>
          
          <motion.p initial="hidden" animate="visible" variants={fadeIn} transition={{ delay: 0.2 }} className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 max-w-2xl mx-auto font-medium">
            SalesWork CRM samler prospektering, pipeline, e-signering og AI-drevet salgscoaching i én elegant plattform. Bygget for ambisiøse nordiske team.
          </motion.p>
          
          <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{ delay: 0.3 }} className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/signup" className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center gap-2">
              Start 14 dagers prøveperiode <SafeIcon icon="➡️" />
            </Link>
            <Link to="/book-demo" className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 backdrop-blur-sm">
              <SafeIcon icon="🎥" /> Book Demo
            </Link>
          </motion.div>
        </div>
      </section>

      {/* --- QUICK FEATURES --- */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-black text-slate-900">Alt du trenger</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white p-8 rounded-3xl border border-slate-200">
                    <SafeIcon icon="🤖" className="text-4xl mb-4" />
                    <h3 className="text-xl font-bold">AI Automasjon</h3>
                    <p className="text-slate-500 mt-2">Spar tid på manuelle oppgaver.</p>
                </div>
                <div className="bg-white p-8 rounded-3xl border border-slate-200">
                    <SafeIcon icon="🏢" className="text-4xl mb-4" />
                    <h3 className="text-xl font-bold">Brreg Integrasjon</h3>
                    <p className="text-slate-500 mt-2">Alltid oppdaterte firmadata.</p>
                </div>
                <div className="bg-white p-8 rounded-3xl border border-slate-200">
                    <SafeIcon icon="✍️" className="text-4xl mb-4" />
                    <h3 className="text-xl font-bold">E-signering</h3>
                    <p className="text-slate-500 mt-2">Signer kontrakter digitalt.</p>
                </div>
            </div>
        </div>
      </section>

      {/* --- Q&A PREVIEW SECTION --- */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-black text-slate-900 mb-6">Ofte stilte spørsmål</h2>
            <div className="grid gap-4 text-left">
                <div className="p-6 bg-slate-50 rounded-2xl">
                    <h4 className="font-bold text-slate-900">Er det bindingstid?</h4>
                    <p className="text-slate-600 mt-2">Nei, ingen bindingstid på månedlige abonnement.</p>
                </div>
                <div className="p-6 bg-slate-50 rounded-2xl">
                    <h4 className="font-bold text-slate-900">Kan jeg betale med faktura?</h4>
                    <p className="text-slate-600 mt-2">Ja, bedrifter kan velge faktura (EHF/PDF) ved registrering.</p>
                </div>
            </div>
            <Link to="/faq" className="inline-block mt-8 text-blue-600 font-bold hover:underline">Se alle spørsmål &rarr;</Link>
        </div>
      </section>

      {/* --- PRICING SECTION --- */}
      <section id="pricing" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
                <h2 className="text-sm font-black text-blue-600 uppercase tracking-widest mb-2">Priser & Abonnement</h2>
                <h3 className="text-4xl font-black text-slate-900">Velg pakken som passer deg</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div className="p-8 rounded-[2rem] border border-slate-200 bg-white hover:border-blue-200 transition-all">
                    <h4 className="text-xl font-black text-slate-900">Start</h4>
                    <p className="text-4xl font-black text-slate-900 mt-4">599,-</p>
                    <Link to="/signup" className="block w-full py-3 mt-8 bg-slate-100 text-slate-700 font-bold text-center rounded-xl hover:bg-slate-200">Velg Start</Link>
                </div>
                <div className="p-8 rounded-[2rem] border-2 border-blue-600 bg-slate-900 text-white relative transform md:-translate-y-4 shadow-2xl">
                    <div className="absolute top-4 right-4 bg-blue-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">Populær</div>
                    <h4 className="text-xl font-black">Vekst</h4>
                    <p className="text-4xl font-black mt-4">990,-</p>
                    <Link to="/signup" className="block w-full py-3 mt-8 bg-blue-600 text-white font-bold text-center rounded-xl hover:bg-blue-500 shadow-lg">Velg Vekst</Link>
                </div>
                <div className="p-8 rounded-[2rem] border border-slate-200 bg-white hover:border-blue-200 transition-all">
                    <h4 className="text-xl font-black text-slate-900">Enterprise</h4>
                    <p className="text-4xl font-black text-slate-900 mt-4">2990,-</p>
                    <Link to="/signup" className="block w-full py-3 mt-8 bg-white border-2 border-slate-900 text-slate-900 font-bold text-center rounded-xl hover:bg-slate-50">Kontakt Oss</Link>
                </div>
            </div>
        </div>
      </section>

      {/* --- FOOTER --- */}
      <footer className="bg-[#0B1426] text-white pt-20 pb-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-black">S</div>
                <span className="text-xl font-black tracking-tight">SalesWork CRM</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                Norges ledende plattform for trygg oppfølging og salgsrapportering.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Løsningen</h4>
              <ul className="space-y-4 text-slate-400 text-sm">
                <li><Link to="/features" className="hover:text-white transition-colors">Funksjoner</Link></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Priser</a></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">Q&A / FAQ</Link></li>
                <li><Link to="/book-demo" className="hover:text-white transition-colors">Book Demo</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Selskap & Juridisk</h4>
              <ul className="space-y-4 text-slate-400 text-sm">
                <li><Link to="/careers" className="hover:text-white transition-colors">Karriere</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Vilkår & Betingelser</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Personvern (GDPR)</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Intern / Partner</h4>
              <ul className="space-y-4 text-slate-400 text-sm">
                <li><Link to="/partner-portal" className="hover:text-white transition-colors">Partner Portal</Link></li>
                <li><Link to="/seller-portal" className="hover:text-white transition-colors">Selger Portal</Link></li>
                {/* FIXED: Link now points to /partner-register */}
                <li><Link to="/partner-register" className="hover:text-white transition-colors">Bli Partner (Registrering)</Link></li>
                <li><Link to="/super-admin" className="hover:text-white transition-colors text-blue-400">SuperAdmin Login</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 text-center text-xs text-slate-500">
            <p>&copy; 2024 SalesWork AS. Alle rettigheter reservert.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;